/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Rentable biz object is the parent for Building, ListingBase and Listable
 */
/* eslint-disable no-nested-ternary */

import { stringToLocalDate } from '@zcode/bizos'
import {
  BuildingLabel,
  PropertyFeatures,
  PropertyType,
  isActive as _isActive,
  isBlocked as _isBlocked,
  isDeleted as _isDeleted,
  isForSale as _isForSale,
  isHoneypot as _isHoneypot,
  isInvalid as _isInvalid,
  isPrivateOrPending as _isPrivateOrPending,
  getEnumTitle,
} from '@zcode/enums'
import {
  toBuildingShortUrl,
  toCityUrl,
  toExternalBuildingUrl,
  toExternalListingUrl,
  toListingShortUrl,
} from '@zcode/nr-utils'
import { dollarize, isNumber } from '@zcode/utils'
import { SHORT_TERM_LEASE_DAYS } from '../constants/filter'
import * as SourceC from '../constants/source'
import * as Utils from '../utils/utils'
import { Bizo, autoclass } from './base'
import * as Media from './media'
import { getAgeText } from './rentable/utils/ageText'
import { getBedsBathsText } from './rentable/utils/getBedsBathsText'
import { getPricePerNight } from './rentable/utils/getPricePerNight'
import { isAgeRestricted } from './rentable/utils/isAgeRestricted'
import { isCustomMessageRequired } from './rentable/utils/isCustomMessageRequired'
import { isExclusive } from './rentable/utils/isExclusive'
import { isIncomeRestricted } from './rentable/utils/isIncomeRestricted'
import { isMilitaryHousingFeature } from './rentable/utils/isMilitaryHousingFeature'
import { isMmPrevent } from './rentable/utils/isMmPrevent'
import { isMoveInDateRequired } from './rentable/utils/isMoveInDateRequired'
import { isPhoneRequired } from './rentable/utils/isPhoneRequired'
import { isRentpath } from './rentable/utils/isRentpath'
import { isRoomForRent } from './rentable/utils/isRoomForRent'
import { isShortTermAvailability } from './rentable/utils/isShortTermAvailability'
import { isShortTermFeed } from './rentable/utils/isShortTermFeed'
import { isShortTermViewV1 } from './rentable/utils/isShortTermViewV1'
import { isStudentHousing } from './rentable/utils/isStudentHousing'
import { isThirdPartyPaid } from './rentable/utils/isThirdPartyPaid'
import { isTour } from './rentable/utils/isTour'
import { isTourDisabled } from './rentable/utils/isTourDisabled'
import { isUnavailable } from './rentable/utils/isUnavailable'
import { isVerified } from './rentable/utils/isVerified'
import { mightHaveInstantTourScheduling } from './rentable/utils/mightHaveInstantTourScheduling'
import * as RentableUtils from './rentable_utils'

const isActive = (item) => _isActive(item.status)
const isBlocked = (item) => _isBlocked(item.status)
const isDeleted = (item) => _isDeleted(item.status)
const isInvalid = (item) => _isInvalid(item.status)
const isForSale = (item) => _isForSale(item.status)
const isHoneypot = (item) => _isHoneypot(item.status)
const isPrivateOrPending = (item) => _isPrivateOrPending(item.status)

/**
 * Ultimately the parent class of:
 * - Building
 * - ListingBase -> Listing > Floorplan
 * - Listable
 */
const Rentable = autoclass('Rentable', class Rentable extends Bizo {}, {
  isActive,
  isBlocked,
  isBuilding: RentableUtils.isBuilding,
  isDeleted,
  isExclusive,
  isExternal: RentableUtils.isExternal,
  isInvalid,
  isForSale,
  isHoneypot,
  isPrivateOrPending,
  isPhoneRequired,
  isMoveInDateRequired,
  isCustomMessageRequired,
  isListing: RentableUtils.isListing,
  isNoDetailPage: RentableUtils.isNoDetailPage,
  // isOpenListing,
  isRentSpecial: RentableUtils.isRentSpecial,
  isRoomForRent,
  isSharedRoom: RentableUtils.isSharedRoom,
  isViewable: RentableUtils.isViewable,
  address: 'address',
  cityState: 'city_state',
  dateAvailable: 'date_available',
  /**
   *
   * @param {import('@zcode/state-type').Rentable} t
   */
  dateAvailableDate: (t) => (t.dateAvailable ? stringToLocalDate(t.dateAvailable) : undefined),
  features: 'features',
  waitlistEnabled: 'waitlist_enabled',
  promotionType: 'promotion_type',
  providerId: 'provider_id',
  shortDescription: 'short_description',
  url: 'url',
  getAgeText,
  getAgeTextMini: (/** @type {import('@zcode/state-type').Rentable} */ t) => getAgeText(t, true),
  displayableAddress: RentableUtils.displayableAddress,
  fullDisplayableAddress: RentableUtils.fullDisplayableAddress,
  displayableArea: RentableUtils.displayableArea,
  groupId: (t) => (t.buildingId ? -1 * t.buildingId : t.listingId),
  hasPropertyMap: (/** @type {import('@zcode/state-type').Rentable} */ t) =>
    !!t.features?.includes(PropertyFeatures.HAS_PROPERTY_MAP),
  headerText: RentableUtils.createListingMainHeader,
  neighborhoodUrl: 'neighborhood_url',
  pricePerNight: getPricePerNight,
  pricePerNightText: RentableUtils.getPricePerNightText,
  priceText: RentableUtils.getPriceText,
  priceTxt: RentableUtils.getPriceTxt,
  rating: 'rating',
  externalPriceTxt: RentableUtils.getExternalPriceTxt,
  /**
   *
   * @param {import('@zcode/state-type').Property | import('@zcode/state-type').Listable} t
   */
  isInactiveListhub: (t) => !!t.feedName?.startsWith('listhub') && !t.isActive,
  isShortTerm: RentableUtils.isShortTerm,
  isShortTermViewV1,
  amenityGroups: 'amenity_groups',
  hasResidentQuotes: (t) => !!t.features?.includes?.(PropertyFeatures.HAS_RESIDENT_QUOTES),
  isBoosted: (t) => !!t.features?.includes?.(PropertyFeatures.BOOSTED),
  isFullServe: (t) => !!t.features?.includes?.(PropertyFeatures.SELECT),
  isFullServeMonetized: (t) => !!t.features?.includes?.(PropertyFeatures.SELECT_MONETIZED),
  isDirectDeal: (t) => !!t.features?.includes?.(PropertyFeatures.DIRECT_DEAL),
  isNewConstruction: (t) => !!t.features?.includes?.(PropertyFeatures.NEW_CONSTRUCTION),
  isShortTermAvailability,
  isThirdPartyPaid,
  isMonetizedM3: (t) => t.isDirectDeal,
  isMonetizedM4: (t) => t.isDirectDeal || t.isThirdPartyPaid,
  isAmpable: (t) => (t.isBuilding && t.state === 'TX') || t.state === 'CA',
  isNoDeposit: (t) => t.features?.includes?.(PropertyFeatures.NO_SECURITY_DEPOSIT),
  isPro: 'is_pro',
  isInstantApplyApplication: (t) =>
    t.features?.includes(PropertyFeatures.INSTANT_APPLY_APPLICATION),
  isInstantApplyScreening: (t) => t.features?.includes(PropertyFeatures.INSTANT_APPLY_SCREENING),
  isInstantApply: (t) => t.isInstantApplyApplication || t.isInstantApplyScreening,
  isInstantApplyEnabled: (t) => t.isListing && t.isPro && t.isInstantApply,
  hasCoordinate: (t) => isNumber(t.lat) && isNumber(t.lng),
  hasSimilar: (t) => t.similar?.some((listable) => listable?.isMessageable),
  hasNoBreedRestrictions: (t) =>
    Boolean(t.features?.includes(PropertyFeatures.NO_BREED_RESTRICTIONS)),
  bedsBathsText: (t) => getBedsBathsText(t, false),
  bedsBathsTxt: (t) => getBedsBathsText(t, true),
  isTrackableBlueshiftRecombee: (t) => {
    return !!(
      t.isMessageable &&
      (!t.isShortTerm || !(isNumber(t.minLeaseDays) && t.minLeaseDays < SHORT_TERM_LEASE_DAYS))
    )
  },
  bedroomTextTitle: (t) => {
    const bedrooms = isNumber(t.bedrooms) ? t.bedrooms : t.minBedrooms
    if (bedrooms === -1) {
      return 'Room'
    }
    return bedrooms === 0 ? 'Studio' : `${bedrooms} Bedroom`
  },
  cityUrl: (t) => {
    if (t.cityState) {
      return toCityUrl({ cityState: t.cityState })
    }
    if (t.neighborhoodUrl) {
      return t.neighborhoodUrl.replace?.(/\/[^/]*$/, '')
    }
    return undefined
  },
  cityStateHood: (t) => {
    if (t.neighborhood) {
      return t.neighborhood.path
    }
    if (t.neighborhoodUrl) {
      return t.neighborhoodUrl.replace?.(/.*\/([^/]+\/[^/]+$)/, '$1')
    }
    return undefined
  },
  shortUrl: (t) =>
    t.buildingId ? toBuildingShortUrl(t.buildingId) : toListingShortUrl(t.listingId),
  externalUrl: (t) =>
    t.isBuilding ? toExternalBuildingUrl(t.buildingId) : toExternalListingUrl(t.listingId),
  isShortTermFeed,
  isAgeRestricted,
  isStudentHousing,
  isMilitaryHousing: (t) => t.buildingLabel === BuildingLabel.MILITARY_HOMES,
  isSpecialtyHousing: (t) =>
    !!t.isAgeRestricted || !!t.isMilitaryHousing || !!t.isStudentHousing || !!t.isIncomeRestricted,
  isVRBO: (t) => t.feedName === SourceC.VRBO,
  isRentalsUnited: (t) => t.feedName === SourceC.RENTALS_UNITED,
  isTour,
  isIncomeRestricted,
  isMilitaryHousingFeature,
  isVerified,
  isMmExclude: (t) => t.features?.includes?.(PropertyFeatures.MM_EXCLUDE),
  isMmPrevent,
  isNoIndex: (t) => t.features?.includes?.(PropertyFeatures.META_NO_INDEX),
  isLiveOnlineTour: (t) => t.features?.includes?.(PropertyFeatures.LIVE_VIRTUAL_TOUR),
  isLuxury: (t) => t.features?.includes(PropertyFeatures.LUXURY),
  isSignatureSubscriptionPackage: (t) =>
    t.features?.includes?.(PropertyFeatures.SIGNATURE_SUBSCRIPTION_PACKAGE),
  isUTMEnabled: (t) => t.features?.includes?.(PropertyFeatures.UTM_ENABLED),
  isUnavailable,
  isZeroDeposit: (t) => t.features?.includes?.(PropertyFeatures.NO_SECURITY_DEPOSIT),
  hasIframeMedia: (t) => t.features?.includes?.(PropertyFeatures.HAS_IFRAME_MEDIA),
  hasVideoMedia: (t) => t.features?.includes?.(PropertyFeatures.HAS_VIDEO_MEDIA),
  isRentpath,
  mightHaveInstantTourScheduling,
  /**
   *
   * @param {import('@zcode/state-type').Property | import('@zcode/state-type').Listable} t
   */
  noFeeLabel: (t) => t.leasingFee === 0 && RentableUtils.isLatLngInNoFeeArea(t.lat, t.lng),
  position: (t) => ({ latitude: t.lat, longitude: t.lng }),
  getDaysOld: (t) => (/** @type {number} */ now) =>
    Math.round(Math.max(0, (now - t.listedOn * 1000) / 86400000)),
  dogsOk: (t) => RentableUtils.checkDogs(t.pets),
  catsOk: (t) => RentableUtils.checkCats(t.pets),
  crmPhone: 'crm_phone',
  crmPhoneHref: (t) => (t.crmPhone ? Utils.toTelHref(t.crmPhone) : undefined),
  paidPhone: (t) => (!t.phone?.includes?.('x') ? t.phone : undefined),
  paidPhoneHref: (t) => (t.paidPhone ? Utils.toTelHref(t.paidPhone) : undefined),
  minPriceText: (t) => dollarize(t.minPrice || t.price || 0),
  maxPriceText: (t) => (t.maxPrice > 0 ? dollarize(t.maxPrice) : t.minPriceText),
  sqftText: (t) => RentableUtils.displaySqFt(t.squareFeet),
  sqftTxt: (t) => RentableUtils.displaySqFt(t.squareFeet, true),
  /**
   *
   * @param {import('@zcode/state-type').Property | import('@zcode/state-type').Listable} t
   */
  sqftRangeTextUnlabeled: (t) => {
    return t.sqftTxt.split(' ')[0]
  },
  // url: (t) => t.isBuilding ? Urls.toBuildingUrl(t.buildingId) : Urls.toListingUrl(t.listingId),
  bathroomAbbrTxt: (t) => {
    const bathrooms = Utils.coalesce(t.maxBathrooms, t.bathrooms)
    return `${bathrooms} bath${bathrooms !== 1 ? 's' : ''}`
  },
  /**
   *
   * @param {import('@zcode/state-type').Property | import('@zcode/state-type').Listable} t
   */
  bathroomRangeTextUnlabeled: (t) => {
    const { maxFullAndHalfBaths: maxBath, minFullAndHalfBaths: minBath } = t
    if (minBath === maxBath || !minBath || !maxBath) {
      if (minBath === 0 && maxBath > 0) {
        return `Up to ${maxBath}`
      }
      if (minBath > 0 && maxBath === 0) {
        return `${minBath}+`
      }
      const value = minBath || maxBath || '\u2013'
      return `${value}`
    }
    return `${minBath}\u2013${maxBath}`
  },
  bathroomTxt: (t) => {
    const bathrooms = Utils.coalesce(t.maxBathrooms, t.bathrooms)
    const s = bathrooms !== 1 ? 's' : ''
    return `${bathrooms} bath${s}`
  },
  bathroomText: (t) => {
    const bathrooms = Utils.coalesce(t.maxBathrooms, t.bathrooms)
    const s = bathrooms !== 1 ? 's' : ''
    return `${bathrooms} Bathroom${s}`
  },
  fullHalfBathroomText: (t) => {
    if (!t.halfBathrooms) {
      return t.bathroomText
    }
    const bathrooms = Utils.coalesce(t.maxBathrooms, t.bathrooms)
    const s = t.halfBathrooms > 1 || (!t.halfBathrooms && bathrooms > 1) ? 's' : ''
    return `${bathrooms} Full${t.halfBathrooms ? `, ${t.halfBathrooms} Half` : ''} Bath${s}`
  },
  minFullAndHalfBaths: (t) => {
    const halfBaths = t.halfBathrooms ? 0.5 : 0
    const minFullBaths = Utils.coalesce(t.minBathrooms, t.bathrooms)
    return typeof minFullBaths === 'number' ? minFullBaths + halfBaths : undefined
  },
  maxFullAndHalfBaths: (t) => {
    const halfBaths = t.halfBathrooms ? 0.5 : 0
    const maxHalfBaths = Utils.coalesce(t.maxBathrooms, t.bathrooms)

    return typeof maxHalfBaths === 'number' ? maxHalfBaths + halfBaths : undefined
  },
  formattedBathroomText: (t) => {
    const { maxFullAndHalfBaths: maxBath, minFullAndHalfBaths: minBath } = t
    if (minBath === maxBath || !minBath || !maxBath) {
      if (minBath === 0 && maxBath > 0) {
        return `Up to ${maxBath} ${maxBath === 1 ? 'Bath' : 'Baths'}`
      }
      if (minBath > 0 && maxBath === 0) {
        return `At least ${minBath} ${minBath === 1 ? 'Bath' : 'Baths'}`
      }
      const value = minBath || maxBath || '\u2013'
      const label = value === 1 ? 'Bath' : 'Baths'
      return `${value} ${label}`
    }
    return `${minBath} - ${maxBath} Baths`
  },
  formattedFullHalfBathroomTxt: (t) => {
    if (!t.halfBathrooms) {
      return t.formattedBathroomText
    }
    const bathrooms = Utils.coalesce(t.maxBathrooms, t.bathrooms)
    const s = t.halfBathrooms > 1 ? 's' : ''
    return `${bathrooms} Full${t.halfBathrooms ? `, ${t.halfBathrooms} Half` : ''} Bath${s}`
  },
  bedroomAbbrTxt: (t) => {
    const bedrooms = isNumber(t.bedrooms) ? t.bedrooms : t.minBedrooms
    if (bedrooms === -1) {
      return 'Room'
    }
    return bedrooms === 0 ? 'Studio' : `${bedrooms} bd`
  },
  /**
   *
   * @param {import('@zcode/state-type').Property | import('@zcode/state-type').Listable} t
   */
  bedroomRangeTextUnlabeled: (t) => {
    const bedrooms = isNumber(t.bedrooms) ? t.bedrooms : t.minBedrooms
    if (bedrooms === -1) {
      return 'Room'
    }
    return bedrooms === 0 ? 'Studio' : `${bedrooms}`
  },
  bedroomAbbrTxtStr: (t) => {
    const bedrooms = isNumber(t.bedrooms) ? t.bedrooms : t.minBedrooms
    if (bedrooms === -1) {
      return 'Room'
    }
    return bedrooms === 0 ? 'Studio' : `${bedrooms} br`
  },
  bedroomTxt: (t) => {
    const bedrooms = isNumber(t.bedrooms) ? t.bedrooms : t.minBedrooms
    const s = bedrooms !== 1 ? 's' : ''
    if (bedrooms === -1) {
      return 'Room'
    }
    return bedrooms === 0 ? 'Studio' : `${bedrooms} Bed${s}`
  },
  bedroomText: (t) => {
    const bedrooms = isNumber(t.bedrooms) ? t.bedrooms : t.minBedrooms
    const s = bedrooms !== 1 ? 's' : ''
    if (bedrooms === -1) {
      return ''
    }
    return bedrooms === 0 ? 'Studio' : `${bedrooms} Bedroom${s}`
  },
  formattedBedroomText: (t) => {
    const minBeds = Utils.coalesce(t.minAllBedrooms, t.minBedrooms, t.bedrooms)
    const maxBeds = Utils.coalesce(t.maxAllBedrooms, t.maxBedrooms, t.bedrooms)
    if (t.isBuilding) {
      if (minBeds !== undefined && maxBeds !== undefined) {
        return RentableUtils.formatBedroomText({ minBeds, maxBeds })
      }
      return 'Unspecified Beds'
    }
    return RentableUtils.formatBedroomText({ minBeds, maxBeds, propertyType: t.propertyType })
  },
  cityStateText: (t) => {
    if (t.city && t.state) {
      return `${t.city}, ${t.state}`
    }
    return ''
  },
  hoodCityText: (t) => {
    if (t.neighborhoodName && t.city) {
      return `${t.neighborhoodName}, ${t.city}`
    }
    return ''
  },
  floorplanText: (t) => {
    const { floorplanCount, isBuilding } = t
    if (isBuilding && floorplanCount) {
      const s = floorplanCount !== 1 ? 's' : ''
      return `${floorplanCount} Floor plan${s}`
    }
    return ''
  },
  images: (t) => t.media?.filter?.((m) => m?.isImage),
  availableImages: (t) => t.images?.filter?.((i) => i.isAvailable !== false),
  mainImage: (t) => {
    const images = t.images
    const imageIds = t.imageIds
    const imageId = imageIds?.length ? imageIds?.[0] : images?.length ? images?.[0]?.mediaId : null
    return Media.getImageUrlBySize({ id: imageId, noImage: true })
  },
  // XL version of first image, or null if there isn't any
  coverImage: (t) => {
    const images = t.images
    const imageIds = t.imageIds
    const imageId = imageIds?.length ? imageIds?.[0] : images?.length ? images?.[0]?.mediaId : null
    return imageId ? Media.getImageUrlBySize({ id: imageId, size: Media.Size.XLARGE }) : null
  },
  hasImage: (t) => !!t.images?.length,
  hasAvailableImage: (t) => !!t.availableImages?.length,
  videos: (t) => t.media?.filter?.((m) => m?.isVideo),
  availableVideos: (t) => t.videos?.filter?.((i) => i.isAvailable !== false),
  iframes: (t) => t.media?.filter?.((m) => m?.isIframe),
  availableIframes: (t) => t.iframes?.filter?.((i) => i.isAvailable !== false),
  hasMedia: (t) => !!t.media?.length,
  availableMedia: (t) => {
    return t.media?.filter?.((i) => i.isAvailable !== false)
  },
  hasAvailableMedia: (t) => !!t.availableMedia?.length,
  availableMediaCount: (t) => (t.availableMedia ? t.availableMedia?.length : 0),
  formattedPriceText: (t) => {
    const { minPrice, maxPrice } = t
    if (minPrice && minPrice === maxPrice) {
      return `${dollarize(minPrice)}`
    }
    if (minPrice && maxPrice) {
      return `${dollarize(minPrice)} to ${dollarize(maxPrice)}`
    }
    if (minPrice) {
      return `${dollarize(minPrice)}`
    }
    return '(undisclosed price)'
  },
  displayPet: (t) => {
    const { dogsOk, catsOk, pets } = t
    if (dogsOk && catsOk) {
      return 'Dogs & Cats'
    }
    if (dogsOk) {
      return 'Dogs'
    }
    if (catsOk) {
      return 'Cats'
    }
    if (pets && !pets.length) {
      return 'No pets'
    }
    if (pets == null) {
      return '\u2014'
    }
    return 'No pets'
  },
  propertyTypeText: (t) => {
    switch (t.propertyType) {
      case PropertyType.CONDO:
      case PropertyType.LOFT:
      case PropertyType.COOP: {
        return getEnumTitle(PropertyType, t.propertyType).toLowerCase()
      }
      case PropertyType.ROOM:
      case PropertyType.SHARED_ROOM: {
        return 'room'
      }
      case PropertyType.TOWNHOUSE:
      case PropertyType.SINGLE_FAMILY_HOME: {
        return 'house'
      }
      case PropertyType.HOTEL: {
        return 'hotel'
      }
      default: {
        return 'apartment'
      }
    }
  },
  isTourDisabled,
})
export default Rentable
